import { useEffect, useState, useCallback } from "react";
import { Activity, CheckCircle2, XCircle, Loader2, Wrench, HardDrive, Mail, Cloud, Database } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, formatDate } from "../lib/api";

function Dot({ ok }) {
  return ok
    ? <CheckCircle2 className="w-4 h-4 text-[#2F6F4E] shrink-0" data-testid="health-ok" />
    : <XCircle className="w-4 h-4 text-[#8A2526] shrink-0" data-testid="health-bad" />;
}

function Row({ icon: Icon, label, ok, detail, testId }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-[#E5E3DB] last:border-0" data-testid={testId}>
      <Icon className="w-4 h-4 text-[#68665E] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1A1A18]">{label}</div>
        {detail && <div className="text-[11px] text-[#68665E] truncate" data-testid={`${testId}-detail`}>{detail}</div>}
      </div>
      <span className={`text-[11px] font-semibold ${ok ? "text-[#2F6F4E]" : "text-[#8A2526]"}`}>{ok ? "Connected" : "Down"}</span>
      <Dot ok={ok} />
    </div>
  );
}

export function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/health"); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load health"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function fixAll() {
    setFixing(true);
    try {
      const { data } = await api.post("/admin/health/fix");
      setData(data.health);
      toast.success(`Fix run — ${(data.actions || []).join(", ") || "no actions"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Fix failed");
    } finally { setFixing(false); }
  }

  const gmail = data?.gmail || {};
  const gmailOk = gmail.configured ? gmail.connected && !gmail.last_error : true;
  const gmailDetail = !gmail.configured ? "Not set up"
    : (gmail.connected ? (gmail.last_error ? `Sync error: ${gmail.last_error}` : (gmail.email || "Connected"))
      : "Not connected — reconnect on Inbox");
  const backup = data?.backup || {};

  return (
    <div className="card-surface p-6 mt-8" data-testid="system-health-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-title flex items-center gap-2"><Activity className="w-4 h-4" /> System Health</div>
          <p className="text-sm text-[#68665E] max-w-xl">Live status of your integrations. If something shows <strong>Down</strong>, hit <strong>Fix it all</strong> to reconnect everything at once.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading || fixing} className="btn-secondary" data-testid="health-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />} Refresh
          </button>
          <button onClick={fixAll} disabled={fixing} className="btn-primary" data-testid="health-fix-all">
            {fixing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />} {fixing ? "Fixing…" : "Fix it all"}
          </button>
        </div>
      </div>

      {data && !data.healthy && (
        <div className="mt-4 text-sm text-[#8A2526] bg-[#FBEAEA] border border-[#F0C9C9] rounded px-3 py-2" data-testid="health-warning">
          {data.problems.length} issue{data.problems.length > 1 ? "s" : ""} detected. Click “Fix it all” to attempt an automatic repair.
        </div>
      )}
      {data && data.healthy && (
        <div className="mt-4 text-sm text-[#2F6F4E] bg-[#E7F2EC] border border-[#BFE0CC] rounded px-3 py-2 flex items-center gap-2" data-testid="health-allgood">
          <CheckCircle2 className="w-4 h-4" /> All systems connected.
        </div>
      )}

      <div className="mt-4 border border-[#E5E3DB] rounded">
        {loading && !data ? (
          <div className="px-3 py-6 text-sm text-[#A09E96] flex items-center gap-2" data-testid="health-loading"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</div>
        ) : data ? (
          <>
            <Row icon={HardDrive} label="Google Drive" ok={!!data.drive?.connected} detail={data.drive?.connected ? (data.drive?.detail || "") : (data.drive?.error || "Not connected")} testId="health-drive" />
            <Row icon={Cloud} label="Object Storage" ok={!!data.storage?.connected} detail={data.storage?.connected ? "" : (data.storage?.error || "Not connected")} testId="health-storage" />
            <Row icon={Mail} label="Company Gmail" ok={gmailOk} detail={gmailDetail} testId="health-gmail" />
            <div className="flex items-center gap-3 px-3 py-2.5" data-testid="health-backup">
              <Database className="w-4 h-4 text-[#68665E] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#1A1A18]">Last Backup</div>
                <div className="text-[11px] text-[#68665E] truncate" data-testid="health-backup-detail">
                  {backup.last_at ? `${formatDate(backup.last_at)} ${String(backup.last_at).slice(11, 16)} · ${backup.count} kept` : "No backups yet"}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-3 py-6 text-sm text-[#A09E96]">Could not load health.</div>
        )}
      </div>
    </div>
  );
}
